// =============================================================================
// SCENARIO TRUTH — the PURE render logic behind the scenarios management
// surface (L-K, the registered R386 follow-up: "FE consumption of
// retiredScenarios[]").
//
// R386 landed the API half — GET /api/scenarios now serves every scenario
// enriched with `retiredStale` / `staleReason` / `staleDetail`, plus
// POST /scenarios/:id/retire and /reactivate (clock-stamped, ownership-scoped,
// NOTHING deleted). Until this lane the front end rendered none of it: the
// owner's pre-correction Australia-move scenario was being held back with no
// way to SEE that, or why.
//
// Everything here is pure (no fetch, no React, no DOM) so the state derivation
// + the plain-language mapping are unit-testable exactly like corpusGrouping.ts
// / alignmentSignalCopy.ts — the house pattern for "logic the screen renders."
//
// NO INVENTION, applied concretely:
//   · A retired row's WHY comes from the served `staleReason` code, mapped to
//     one of two fixed sentences. An UNRECOGNISED code maps to null — the row
//     renders as retired with no invented explanation, never a bare code string
//     and never a guessed reason.
//   · The DETAIL is the server's own `staleDetail` sentence, rendered verbatim.
//     This module never composes a detail sentence of its own (it does not know
//     the countries involved — only the server does).
//   · `canReactivate` keys off `retiredAt` — the field POST /reactivate
//     literally clears — never off a guess about whether the button would work.
//     See its doc comment for the honesty argument in full.
// =============================================================================

import type { ScenarioRecord } from './api';

/** The action a row can currently have in flight. */
export type ScenarioPendingAction = 'retire' | 'reactivate';

/** A single row's in-flight marker, owned by the caller (the hook), so the
 *  panel can grey exactly ONE row rather than the whole list. */
export interface ScenarioPending {
  id: string;
  action: ScenarioPendingAction;
}

/** The four states the style guide pins: a live scenario, a retired one, and
 *  the two pending states while an action is in flight. (An action FAILURE is
 *  not a row state — it surfaces once, at panel level, the way
 *  CorpusArchivePanel's own error banner does.) */
export type ScenarioRowState = 'active' | 'retired' | 'retiring' | 'reactivating';

/** The two reason codes apps/api/lib/scenarioStaleness.ts emits today. Kept as
 *  a named union for the mapping below, while `ScenarioRecord.staleReason`
 *  stays a plain `string | null` on the wire — so a code this build has never
 *  seen still type-checks its way to the "no invented why" branch instead of
 *  being silently coerced into one of these two. */
export type KnownStaleReason = 'user_retired' | 'contradicts_home_no_declared_intent';

/** Retired-stale per the server's own derived verdict — the SAME verdict the
 *  serve paths enforce (apps/api/lib/scenarios.ts's partition + the
 *  loadActiveScenarios WHERE), so this surface can never disagree with what is
 *  actually being scored. */
export function isRetiredScenario(s: ScenarioRecord): boolean {
  return s.retiredStale === true;
}

/** The live scenarios — rendered normally, each offering Retire. */
export function servableScenarios(scenarios: ScenarioRecord[]): ScenarioRecord[] {
  return scenarios.filter((s) => !isRetiredScenario(s));
}

/** The retired ones — rendered greyed in the archive idiom, with the why. */
export function retiredScenarios(scenarios: ScenarioRecord[]): ScenarioRecord[] {
  return scenarios.filter(isRetiredScenario);
}

/** Which of the four states this row is in right now. A pending action for a
 *  DIFFERENT row never changes this row's state (one in-flight marker, scoped
 *  by id — the same discipline useSettings applies to its per-action phase
 *  machines). */
export function scenarioRowState(
  s: ScenarioRecord,
  pending: ScenarioPending | null,
): ScenarioRowState {
  if (pending && pending.id === s.id) {
    return pending.action === 'retire' ? 'retiring' : 'reactivating';
  }
  return isRetiredScenario(s) ? 'retired' : 'active';
}

/**
 * Can POST /scenarios/:id/reactivate actually bring this one back?
 *
 * ONLY when a retire STAMP exists to clear — `retiredAt`. That is the whole of
 * what the endpoint does (sets retired_at/retired_reason to null).
 *
 * A DERIVED-stale scenario (retiredStale true, retiredAt null — its target
 * country contradicts the resolved home country with no declared move) has no
 * stamp: calling reactivate on it would clear nothing, the refetch would serve
 * it still retired, and a "bring it back" button would have lied. So we do not
 * offer one — we tell the truth about what WOULD bring it back instead
 * (describeReactivationPath), which is the same honest path the API's own route
 * comment names: declare the move.
 */
export function canReactivate(s: ScenarioRecord): boolean {
  return isRetiredScenario(s) && s.retiredAt != null;
}

/**
 * The plain-language WHY for a retired row — Meira's voice, first person.
 * Never a bare code string; null for a reason code this build does not know
 * (render the row as retired, explain nothing we cannot ground).
 *
 * Both sentences' "I've stopped scoring jobs against it" clause is TRUE of both
 * retire kinds, verified in code, not assumed: an explicit retire is dropped by
 * loadActiveScenarios' `isNull(retired_at)` WHERE (apps/api/lib/scenarios.ts),
 * and a derived-stale one is dropped by partitionScenariosByStaleness on both
 * the re-score path (scenarios.ts) and the serve path (jobs.routes.ts).
 */
export function describeStaleHeadline(s: ScenarioRecord): string | null {
  if (!isRetiredScenario(s)) return null;
  switch (s.staleReason) {
    case 'user_retired':
      return "You retired this one, so I've stopped scoring jobs against it.";
    case 'contradicts_home_no_declared_intent':
      return "This one's out of step with where you're based, so I've stopped scoring jobs against it.";
    default:
      return null;
  }
}

/** The one served detail that carries NOTHING the headline above it does not
 *  already say, in Meira's voice — scenarioStaleness.ts emits it as a fixed
 *  constant for the explicit-retire branch. Matched by exact STRING, not by
 *  reason code, deliberately: if the server ever changes that sentence to
 *  something informative, it starts rendering again on its own instead of
 *  staying silently hidden behind a code check. */
const REDUNDANT_STALE_DETAIL = 'You retired this scenario.';

/** The server's own detail sentence, verbatim — the specifics this app is
 *  never told (which countries, which posture) and therefore must never write
 *  for itself. Trimmed-empty reads as absent (an empty line is not
 *  information), as does the one restatement above. */
export function describeStaleDetail(s: ScenarioRecord): string | null {
  const detail = (s.staleDetail ?? '').trim();
  if (detail.length === 0) return null;
  if (detail === REDUNDANT_STALE_DETAIL && describeStaleHeadline(s) !== null) return null;
  return detail;
}

/** The user's own free-text note from when they retired it, if any — served on
 *  the row (`retired_reason`). Verbatim; never paraphrased. */
export function describeRetiredNote(s: ScenarioRecord): string | null {
  const note = (s.retiredReason ?? '').trim();
  return note.length > 0 ? note : null;
}

/**
 * For a retired row we CANNOT reactivate (derived-stale, no stamp): the honest
 * route back. Grounded in the derivation itself — scenarioStaleness.ts keeps a
 * scenario the moment the profile declares intent toward its country, so
 * declaring the move genuinely revives it. Deliberately does NOT name a
 * country (this module is never told which one; the served detail already says
 * it) and does NOT promise a specific button.
 *
 * Null whenever a real Reactivate is on offer — one route back per row, never
 * two competing instructions.
 */
export function describeReactivationPath(s: ScenarioRecord): string | null {
  if (!isRetiredScenario(s) || canReactivate(s)) return null;
  return "Nothing is deleted — it's kept exactly as you left it. Tell me that move is part of your plan and it comes back on its own.";
}

const WORK_AUTHORIZATION_LABEL: Record<string, string> = {
  au_needs_sponsorship: 'Australia — needs sponsorship',
  au_partner_visa: 'Australia — partner visa',
  us_authorized: 'US — already authorized',
  other: 'Other',
};

/** The served work-authorization enum in plain words. An unrecognised value
 *  reads as null (omitted from the meta line) rather than leaking the raw
 *  column value into the UI. */
export function describeWorkAuthorization(code: string | null | undefined): string | null {
  if (!code) return null;
  return WORK_AUTHORIZATION_LABEL[code] ?? null;
}

/**
 * The quiet meta line under a scenario's name — every part served, none
 * inferred: work authorization in plain words · the location tokens verbatim ·
 * "remote ok" only when remoteOk is true · "paused" only when the row's own
 * `active` flag is false (a pause is a different thing from a retire, and
 * rendering a paused scenario as live would be a lie). Null when the row
 * carries nothing to say.
 */
export function describeScenarioMeta(s: ScenarioRecord): string | null {
  const parts: string[] = [];
  const auth = describeWorkAuthorization(s.workAuthorization);
  if (auth) parts.push(auth);
  const locations = (s.locations ?? []).map((l) => l.trim()).filter((l) => l.length > 0);
  if (locations.length > 0) parts.push(locations.join(' / '));
  if (s.remoteOk) parts.push('remote ok');
  if (s.active === false) parts.push('paused');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The honest failure line — action-specific, carrying the real served message
 *  rather than swallowing it. Matches CorpusArchivePanel's own "surface the
 *  message, never a silent catch" shape. */
export function describeScenarioActionError(
  action: ScenarioPendingAction,
  message: string | null | undefined,
): string {
  const detail = (message ?? '').trim();
  const opening =
    action === 'retire' ? "I couldn't retire that one" : "I couldn't bring that one back";
  return detail.length > 0 ? `${opening}: ${detail}` : `${opening} — try again in a moment.`;
}

/** The retire/reactivate button label for a row, including its in-flight
 *  wording (the pending state the style guide pins). */
export function describeScenarioActionLabel(state: ScenarioRowState): string | null {
  switch (state) {
    case 'active':
      return 'Retire';
    case 'retired':
      return 'Bring it back';
    case 'retiring':
      return 'Retiring…';
    case 'reactivating':
      return 'Bringing it back…';
    default:
      return null;
  }
}

/** The collapsed archive toggle's honest count — "Retired (2)". Never a
 *  rounded or invented number; it is the length of what was served. */
export function describeRetiredCount(scenarios: ScenarioRecord[]): number {
  return retiredScenarios(scenarios).length;
}
